import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Link2,
  AlertTriangle,
  FileText,
  Calendar,
  Users,
  Briefcase,
  ChevronRight,
  Loader2,
  XCircle,
  CheckCircle2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useFormatters } from "../shared/useFormatters";
import api, { extractErrorMessage } from "../../lib/api";

const TYPE_ICONS = {
  project: Briefcase,
  quote: FileText,
  invoice: FileText,
  shift: Calendar,
  shift_assignment: Users,
  task_assignment: Users,
  shift_task: Calendar,
};

const TYPE_COLORS = {
  project: "text-blue-400",
  quote: "text-purple-400",
  invoice: "text-green-400",
  shift: "text-yellow-400",
  shift_assignment: "text-orange-400",
  task_assignment: "text-orange-400",
  shift_task: "text-yellow-400",
};

/**
 * DependencyPreviewPanel - Shows detailed dependency information for selected records
 * 
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - entityType: string (clients, staff, locations, task_definitions)
 * - recordIds: string[] - IDs of selected records
 * - recordNames: Map<string, string> - Map of ID to display name
 * - onConfirmDelete: () => void - Called when user confirms deletion (only if allowed)
 */
export function DependencyPreviewPanel({
  open,
  onOpenChange,
  entityType,
  recordIds = [],
  recordNames = {},
  onConfirmDelete,
}) {
  const { formatDate } = useFormatters();
  const [loading, setLoading] = useState(false);
  const [dependencyData, setDependencyData] = useState({}); // recordId -> dependency details
  const [expandedRecords, setExpandedRecords] = useState(new Set());

  // Load dependency details when panel opens
  useEffect(() => {
    if (open && recordIds.length > 0) {
      loadDependencies();
    }
  }, [open, recordIds]);

  const loadDependencies = async () => {
    setLoading(true);
    const data = {};
    
    try {
      // Fetch dependencies for each selected record
      const results = await Promise.all(
        recordIds.map(async (id) => {
          try {
            const response = await api.get(`/cleanup/dependencies/${entityType}/${id}`);
            return { id, data: response.data };
          } catch (err) {
            console.error(`Failed to load dependencies for ${id}:`, err);
            return { id, data: { total: 0, dependents: [], error: true } };
          }
        })
      );
      
      results.forEach(({ id, data: depData }) => {
        data[id] = depData;
      });
      
      setDependencyData(data);
      // Auto-expand first record if only one
      if (recordIds.length === 1) {
        setExpandedRecords(new Set([recordIds[0]]));
      }
    } catch (err) {
      console.error("Failed to load dependencies:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (recordId) => {
    const newExpanded = new Set(expandedRecords);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRecords(newExpanded);
  };

  // Calculate summary stats
  const totalDependencies = Object.values(dependencyData).reduce(
    (sum, d) => sum + (d?.total || 0), 0
  );
  const canDeleteAny = Object.values(dependencyData).some(d => (d?.total || 0) === 0);
  const canDeleteAll = Object.values(dependencyData).every(d => (d?.total || 0) === 0);
  const blockedCount = Object.values(dependencyData).filter(d => (d?.total || 0) > 0).length;

  // Render dependent item
  const renderDependent = (dep) => {
    const Icon = TYPE_ICONS[dep.type] || FileText;
    const colorClass = TYPE_COLORS[dep.type] || "text-muted-foreground";

    return (
      <div
        key={`${dep.type}-${dep.id}`}
        className="flex items-center gap-3 py-2 px-3 bg-white/5 rounded-lg"
      >
        <Icon className={cn("h-4 w-4", colorClass)} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{dep.name}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="capitalize">{dep.type.replace(/_/g, " ")}</span>
            {dep.date && (
              <>
                <span>•</span>
                <span>{formatDate(dep.date)}</span>
              </>
            )}
            {dep.status && (
              <>
                <span>•</span>
                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                  {dep.status}
                </Badge>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Dependency Preview
          </DialogTitle>
          <DialogDescription>
            Review dependencies before deleting {recordIds.length} {entityType.replace(/_/g, " ")}
            {recordIds.length !== 1 ? "s" : ""}.
          </DialogDescription>
        </DialogHeader>

        {/* Summary Banner */}
        <div className={cn(
          "flex items-center gap-3 p-4 rounded-lg border",
          canDeleteAll 
            ? "bg-green-500/10 border-green-500/20" 
            : "bg-yellow-500/10 border-yellow-500/20"
        )}>
          {canDeleteAll ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          )}
          <div className="flex-1">
            {canDeleteAll ? (
              <p className="text-sm text-green-400">
                All {recordIds.length} record{recordIds.length !== 1 ? "s" : ""} can be safely deleted.
              </p>
            ) : (
              <p className="text-sm text-yellow-400">
                {blockedCount} of {recordIds.length} record{recordIds.length !== 1 ? "s" : ""} have dependencies 
                ({totalDependencies} total) and cannot be deleted.
              </p>
            )}
          </div>
        </div>

        {/* Records List */}
        <ScrollArea className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3 pr-4">
              {recordIds.map((recordId) => {
                const deps = dependencyData[recordId] || { total: 0, dependents: [] };
                const displayName = recordNames[recordId] || recordId.substring(0, 8) + "...";
                const isExpanded = expandedRecords.has(recordId);
                const canDelete = deps.total === 0;

                return (
                  <div
                    key={recordId}
                    className={cn(
                      "border rounded-lg overflow-hidden",
                      canDelete ? "border-green-500/20" : "border-yellow-500/20"
                    )}
                  >
                    {/* Record Header */}
                    <button
                      className={cn(
                        "w-full flex items-center gap-3 p-3 text-left transition-colors",
                        "hover:bg-white/5",
                        isExpanded && "bg-white/5"
                      )}
                      onClick={() => toggleExpanded(recordId)}
                      data-testid={`expand-record-${recordId}`}
                    >
                      <ChevronRight className={cn(
                        "h-4 w-4 transition-transform",
                        isExpanded && "rotate-90"
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{displayName}</p>
                        <p className="text-xs text-muted-foreground">
                          {deps.total === 0 
                            ? "No dependencies" 
                            : `${deps.total} dependenc${deps.total === 1 ? "y" : "ies"}`
                          }
                        </p>
                      </div>
                      {canDelete ? (
                        <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
                          Safe to Delete
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-400 hover:bg-red-500/30">
                          <XCircle className="h-3 w-3 mr-1" />
                          Blocked
                        </Badge>
                      )}
                    </button>

                    {/* Expanded Dependencies */}
                    {isExpanded && deps.dependents && deps.dependents.length > 0 && (
                      <div className="p-3 pt-0 space-y-2 border-t border-white/10">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider pt-2">
                          Linked Records
                        </p>
                        {deps.dependents.map(renderDependent)}
                      </div>
                    )}

                    {/* No dependencies message */}
                    {isExpanded && deps.total === 0 && (
                      <div className="p-3 pt-0 border-t border-white/10">
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No dependencies found. This record can be safely deleted.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="gap-2 pt-4 border-t border-white/10">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canDeleteAny}
            onClick={onConfirmDelete}
            data-testid="confirm-delete-btn"
          >
            {canDeleteAll ? (
              `Delete ${recordIds.length} Record${recordIds.length !== 1 ? "s" : ""}`
            ) : canDeleteAny ? (
              `Delete ${recordIds.length - blockedCount} Safe Record${recordIds.length - blockedCount !== 1 ? "s" : ""}`
            ) : (
              "Cannot Delete (All Blocked)"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DependencyPreviewPanel;
